/**
 * Seite „Bestellungen" der Sektion „Anfos & Umsatz" (P5 Kern, Plan 2026-10-27-p4-hebel-p5-kern Paket B). Löst die alte
 * Anfo-Übersicht /plx-orders ab: frei wählbarer Zeitraum (URL `startDate`/`endDate`, Standard die letzten 7 Tage,
 * Schnellwahl 7 T / letzte KW / 30 T / 3 M, „Daten bis" = PL-X-Stand), oben die Kacheln aus der Gesamtzeile, darunter die
 * Karte „Verweigerte Bestellungen", vier Übersichten Produkt / Kanal / Blog / WKZ mit Excel und die bestehende
 * Bestellliste (`plx-orders/OrderList.tsx`, wiederverwendet, Sektions-Modus) mit Netto-Spalte und Bestell-Detail.
 * `?suche=` belegt die Suche der Liste vor und bleibt in der URL (Links aus der Webinar-Detailseite und der Umleitung).
 * Backend: /api/auswertungen/bestellungen (`bestellungenService.ts`). Alle Euro netto.
 *
 * Zeilen und Summenzeile kommen aus DERSELBEN Antwort, und eine Antwort wird nur gezeigt, wenn sie zum aktuell gewählten
 * Zeitraum gehört (Schlüssel `von|bis`) — ein schneller Doppelwechsel kann so keine alten Zeilen unter einer neuen
 * Summenzeile zeigen (Falle der alten Seite, `.claude/rules/react-frontend.md` § TableWidget).
 */
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Link, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import ChapterPage from '../../../components/auswertungen/ChapterPage';
import { fest, formatDelta, Kachel, KACHEL_RASTER, tEuro } from '../../../components/auswertungen/kennzahlTeile';
import OrderDetailsModal from '../../../components/OrderDetailsModal';
import { FilterContext, IFilterState } from '../../../contexts/FilterContext';
import { BestellungenAntwort, BestellVergleich, BestellVergleichWert, fetchBestellungen } from '../../../helpers/bestellungenData';
import { germanNow } from '../../../helpers/germanTime';
import { formatEur, formatPct, TERMS } from '../../../helpers/kpiDefinitions';
import { formatNumber } from '../../../helpers/numberUtils';
import OrderList from '../../plx-orders/OrderList';
import { altFilterHinweis } from '../umleitungsTabelle';
import DatumFeld, { datumGueltig } from './DatumFeld';
import { kachelDelta, vergleichEtikett } from './kachelVergleich';
import UebersichtKarte, { BlogHinweis } from './UebersichtKarte';
import VerweigerteKarte from './VerweigerteKarte';

/**
 * Gültiger Tag ab 2020-01-01 (`DatumFeld.datumGueltig`). Ohne Untergrenze war beim Tippen der Jahreszahl „0002-10-01"
 * gültig, und `lokal()` machte daraus 1902 — die Liste lud die ganze Historie (Review 27.10.). Gilt auch für die URL.
 */
const gueltig = (v: string | null): v is string => datumGueltig(v);

/**
 * Schnellwahl: Tage bis heute (inklusive), die vorige volle Kalenderwoche (Mo–So, dieselben Wochen wie die Karte
 * „Verweigerte Bestellungen") bzw. drei Kalendermonate.
 */
const SCHNELLWAHL: { key: string; label: string; tip?: string; zeitraum: (heute: string) => { von: string; bis: string } }[] = [
  { key: '7', label: '7 T', zeitraum: (h) => ({ von: moment(h).subtract(6, 'days').format('YYYY-MM-DD'), bis: h }) },
  {
    key: 'kw',
    label: TERMS.letzteKw.label,
    tip: TERMS.letzteKw.tip,
    zeitraum: (h) => {
      const montag = moment(h, 'YYYY-MM-DD').isoWeekday(1).subtract(7, 'days');
      return { von: montag.format('YYYY-MM-DD'), bis: montag.clone().add(6, 'days').format('YYYY-MM-DD') };
    },
  },
  { key: '30', label: '30 T', zeitraum: (h) => ({ von: moment(h).subtract(29, 'days').format('YYYY-MM-DD'), bis: h }) },
  {
    key: '3m',
    label: '3 M',
    zeitraum: (h) => ({ von: moment(h).subtract(3, 'months').add(1, 'day').format('YYYY-MM-DD'), bis: h }),
  },
];

/** PL-X-Stand `YYYY-MM-DD HH:MM:SS` (Berliner Wandzeit) → `26.10.2026 20:50`. */
const standText = (v: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(v);
  return m ? `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}` : v;
};
const n = (v: number) => formatNumber(v, 0);
// Vier Kacheln wie P1–P4 (`Kachel`), breit 4 nebeneinander, sonst 2 × 2 — auch bei 390 px (Persona-Umbau A5).
const reihe = { ...KACHEL_RASTER, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' } } as const;

/**
 * Zeile „+12 % gegen Ø der 12 Wochen davor" unter einer Kachel (A5). ▲/▼ nur außerhalb der Spanne der Vergleichszeiträume;
 * der Tooltip nennt Schnitt, Spanne und — falls gekürzt — die Stelle, bis zu der jeder Zeitraum zählt.
 */
const VergleichZeile: FC<{
  jetzt: number;
  v: BestellVergleichWert;
  vgl: BestellVergleich;
  format: (x: number) => string;
  /** Mehr ist schlecht (verweigert): ▲ rot, ▼ grün. */
  mehrIstSchlecht?: boolean;
}> = ({ jetzt, v, vgl, format, mehrIstSchlecht = false }) => {
  const d = kachelDelta(jetzt, v, vgl.zeitraeume, vgl.marke_ab);
  const spanne = vgl.zeitraeume > 1 ? `, Spanne ${format(v.min)}–${format(v.max)}` : '';
  const gekuerzt =
    vgl.gekuerzt_auf_stunden !== null
      ? ` Der gewählte Zeitraum reicht über den Datenstand hinaus; jeder Vergleichszeitraum zählt deshalb nur gleich lange ab seinem Start, also bis zur selben Uhrzeit wie der Datenstand.`
      : '';
  const tip = `${vgl.zeitraeume > 1 ? 'Ø' : 'Wert'} ${format(v.schnitt)}${spanne} (${moment(vgl.von).format('DD.MM.YYYY')}–${moment(vgl.bis_exklusiv).subtract(1, 'day').format('DD.MM.YYYY')}).${gekuerzt} ${TERMS.bestellVergleich.tip}`;
  return (
    <Tooltip title={tip} arrow placement="bottom">
      <Box component="span" sx={{ display: 'block', cursor: 'help', fontVariantNumeric: 'tabular-nums' }}>
        {/* Pfeil VOR der Zahl wie überall in der Sektion („▲ +30 %"), Runde 2. */}
        {d.marke && (
          <Box
            component="span"
            aria-label={d.marke === 'up' ? 'auffällig höher' : 'auffällig niedriger'}
            sx={{ mr: 0.5, fontSize: 12, color: (d.marke === 'up') !== mehrIstSchlecht ? '#2e7d32' : '#c62828' }}
          >
            {d.marke === 'up' ? '▲' : '▼'}
          </Box>
        )}
        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {d.delta === null ? 'neu' : fest(formatDelta(d.delta))}
        </Box>{' '}
        {vergleichEtikett(vgl)}
      </Box>
    </Tooltip>
  );
};

/** `YYYY-MM-DD` → lokales Datum (Mitternacht), so wie der Datepicker der alten Seite (OrderList liest lokale Teile). */
const lokal = (v: string): Date => {
  const [y, m, d] = v.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const tagText = (v: string): string => moment(v, 'YYYY-MM-DD').format('DD.MM.YYYY');

const Bestellungen: FC = () => {
  const [params, setParams] = useSearchParams();
  const heute = useMemo(() => germanNow().format('YYYY-MM-DD'), []);
  const rohVon = params.get('startDate');
  const rohBis = params.get('endDate');
  const bis = gueltig(rohBis) && rohBis <= heute ? rohBis : heute;
  const von = gueltig(rohVon) && rohVon <= bis ? rohVon : SCHNELLWAHL[0].zeitraum(bis).von;
  const schluessel = `${von}|${bis}`;
  const schnell =
    SCHNELLWAHL.find((s) => {
      const z = s.zeitraum(heute);
      return z.von === von && z.bis === bis;
    })?.key ?? null;
  // Link von der alten /plx-orders mit Filtern, die diese Seite nicht kennt (Umleitung hängt sie als `alt` an).
  const altFilter = params.get('alt');
  const altHinweis = altFilter !== null ? altFilterHinweis(altFilter, von, bis) : null;
  // Suchbegriff der Bestellliste (Links mit `?suche=`; jede Eingabe schreibt ihn zurück).
  const suche = params.get('suche') ?? '';
  // Schnellfilter der Liste von außen (`?liste=verweigert`, Link „+ N verweigert“ der Webinar-Detailseite).
  const liste = params.get('liste');
  const setzeSuche = useCallback(
    (v: string) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (v) next.set('suche', v);
          else next.delete('suche');
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );

  // Schnellfilter der Liste zurück in die URL — sonst trüge ein geteilter Link nach „Alle“ weiter `liste=verweigert`.
  const setzeListe = useCallback(
    (k: string) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (k && k !== 'alle') next.set('liste', k);
          else next.delete('liste');
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  // Der Hinweis „Liste gefiltert“ gilt dem Filter, mit dem die Seite GEÖFFNET wurde (Link der Detailseite). Eigene
  // Eingaben in der Liste lösen ihn nicht aus — vorher erschien er beim ersten getippten Buchstaben über der Seite und
  // schob die Liste 78 px nach unten; nach einem Wechsel des Schnellfilters blieb er stehen (Bedienprüfung 28.10.).
  const [linkFilter] = useState(() => (suche || liste ? { suche, liste } : null));
  const linkFilterAktiv = linkFilter !== null && linkFilter.suche === suche && linkFilter.liste === liste;

  const setzeZeitraum = useCallback(
    (neuVon: string, neuBis: string) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('startDate', neuVon);
          next.set('endDate', neuBis);
          return next;
        },
        { replace: false },
      ),
    [setParams],
  );

  const [stand, setStand] = useState<{ key: string; data: BestellungenAntwort | null; fehler: string | null } | null>(null);
  useEffect(() => {
    let abgebrochen = false;
    fetchBestellungen(von, bis)
      .then((data) => {
        if (!abgebrochen) setStand({ key: `${von}|${bis}`, data, fehler: null });
      })
      .catch((e: unknown) => {
        if (!abgebrochen) setStand({ key: `${von}|${bis}`, data: null, fehler: e instanceof Error ? e.message : String(e) });
      });
    return () => {
      abgebrochen = true;
    };
  }, [von, bis]);
  // Nur eine Antwort zum GEWÄHLTEN Zeitraum zeigen — sonst „lädt".
  const aktuell = stand && stand.key === schluessel ? stand : null;
  const data = aktuell?.data ?? null;
  const fehler = aktuell?.fehler ?? null;

  // Bestellliste: eigener Filter (nur Zeitraum, Testbestellungen aus) statt der Seitenfilter der alten /plx-orders.
  const kontext = useContext(FilterContext)!;
  const basis = kontext.filterState;
  const listenFilter: IFilterState = useMemo(
    () => ({ ...basis, startDate: lokal(von), endDate: lokal(bis), custom: { excludeTestOrders: true } }),
    // basis bewusst nicht: die Liste liest nur Zeitraum und custom (plxListFilterOptions).
    [von, bis],
  );
  const [bestellung, setBestellung] = useState<string | null>(null);
  const oeffne = useCallback((nr: string) => setBestellung(nr), []);

  // Mit `?suche=`/`?liste=` (Link von der Webinar-Detailseite) einmal zur Liste springen — erst wenn Übersichten UND Liste
  // geladen sind, sonst verschiebt der Nachlauf darüber die Stelle wieder.
  const [listenAnzahl, setListenAnzahl] = useState<number | null>(null);
  const sprung = useRef(Boolean(suche || liste) && altFilter === null);
  const zurListe = useCallback(() => document.getElementById('bestellliste')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), []);
  useEffect(() => {
    if (!sprung.current || !data || listenAnzahl === null) return;
    sprung.current = false;
    requestAnimationFrame(zurListe);
  }, [data, listenAnzahl, zurListe]);

  const zeitraumText = `${tagText(von)}–${tagText(bis)}`;
  const gesamt = data?.uebersichten.produkt.total ?? null;
  const kachelWert = (f: (t: NonNullable<typeof gesamt>) => string) => (gesamt ? f(gesamt) : fehler ? '—' : '…');
  const karten = {
    zeitraumText,
    data,
    fehler,
    dateiBasis: `bestellungen_${von}_${bis}`,
  };

  return (
    <ChapterPage
      title="Bestellungen"
      question="Was wurde im gewählten Zeitraum bestellt — nach Produkt, Kanal, Blog und Werbeaktion?"
      intro={
        <Typography component="span" variant="body2" sx={{ color: 'text.secondary', display: 'block', mt: 1, maxWidth: '75ch' }}>
          Frei wählbarer Zeitraum, alle Euro netto (ohne MwSt.). {TERMS.bestellungenAlle.label} = {TERMS.anfos.label} + verweigerte.
          Alle Quoten und Euro-Werte rechnen mit {TERMS.anfos.label} — außer dem Anteil verweigert (der zählt gegen die{' '}
          {TERMS.bestellungenAlle.label}).
        </Typography>
      }
      standQuellen={[
        { key: 'plx_orders', label: 'Bestellsystem (PL-X)' },
        { key: 'anfo_werte', label: 'Anfo-Werte' },
      ]}
    >
      {altHinweis && (
        <Alert severity="info" sx={{ mb: 2, maxWidth: '90ch' }}>
          Der aufgerufene Link stammt von der alten Anfo-Übersicht und hatte Filter
          {altHinweis.filter.length > 0 ? ` (${altHinweis.filter.join(', ')})` : ''}, die diese Seite nicht kennt — hier gilt
          nur der Zeitraum{suche ? `; die Bestellliste unten sucht nach „${suche}“` : ''}. Mit den Filtern und dem gewählten
          Zeitraum:{' '}
          <Link component={RouterLink} to={altHinweis.link} underline="hover">
            alte Anfo-Übersicht öffnen
          </Link>
          .
        </Alert>
      )}
      {!altHinweis && linkFilterAktiv && (suche || liste === 'verweigert') && (
        <Alert severity="info" sx={{ mb: 2, maxWidth: '90ch' }}>
          Die Bestellliste unten ist gefiltert{suche ? ` nach „${suche}“` : ''}
          {liste === 'verweigert' ? ', nur verweigerte' : ''}. Kacheln und Übersichten zeigen den ganzen Zeitraum.{' '}
          <Link component="button" onClick={zurListe} underline="hover" sx={{ verticalAlign: 'baseline' }}>
            Zur Liste
          </Link>
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          mb: 2,
          p: 1.5,
          border: 1,
          borderColor: 'divider',
          borderRadius: '8px',
        }}
      >
        {/* Ein Anfang nach dem Ende schiebt das Ende mit (und umgekehrt), wie bisher. */}
        <DatumFeld label="Von" value={von} max={heute} onGueltig={(v) => setzeZeitraum(v, v > bis ? v : bis)} />
        <DatumFeld label="Bis" value={bis} max={heute} onGueltig={(v) => setzeZeitraum(v < von ? v : von, v)} />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={schnell}
          onChange={(_e, k: string | null) => {
            const s = SCHNELLWAHL.find((x) => x.key === k);
            if (s) {
              const z = s.zeitraum(heute);
              setzeZeitraum(z.von, z.bis);
            }
          }}
          aria-label="Schnellwahl Zeitraum"
        >
          {SCHNELLWAHL.map((s) => (
            <ToggleButton key={s.key} value={s.key} sx={{ px: 1.5, whiteSpace: 'nowrap' }} title={s.tip}>
              {s.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {zeitraumText}
          {data ? ` · ${data.zeitraum.tage} ${data.zeitraum.tage === 1 ? 'Tag' : 'Tage'}` : ''}
          {data?.datenstand && (
            <Tooltip title={TERMS.datenBis.tip} arrow>
              <span>
                {' · '}
                {TERMS.datenBis.label} {standText(data.datenstand)}
              </span>
            </Tooltip>
          )}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          size="small"
          variant="outlined"
          onClick={() => document.getElementById('bestellungen')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          sx={{ color: 'text.primary', borderColor: 'divider' }}
        >
          Zur Bestellliste
        </Button>
      </Box>

      {/*
        Kacheln = Gesamtzeile der Übersichten (dieselbe Antwort, `uebersichten.produkt.total`). A5: „Bisher bezahlt" ist bei
        Anfos von wenigen Tagen fast 0 und sagt nichts — an seiner Stelle der Erwartete Wert (dieselbe Summe wie die Spalte der
        Übersichten). Jede Kachel mit Δ gegen gleich lange Zeiträume davor (Backend `bestellVergleich`).
      */}
      <Box sx={reihe} aria-label="Kennzahlen des Zeitraums">
        <Kachel
          wert={kachelWert((t) => n(t.bestellungen))}
          label={TERMS.bestellungenAlle.label}
          tip={TERMS.bestellungenAlle.tip}
          sub={
            <>
              {TERMS.anfos.label} + verweigerte
              {gesamt && data?.vergleich && <VergleichZeile jetzt={gesamt.bestellungen} v={data.vergleich.bestellungen} vgl={data.vergleich} format={(x) => formatNumber(x, x < 10 ? 1 : 0)} />}
            </>
          }
        />
        <Kachel
          wert={kachelWert((t) => n(t.anfos))}
          label={TERMS.anfos.label}
          tip={`${TERMS.anfos.tip} ${TERMS.vorlaeufig.tip}`}
          sub={
            <>
              {gesamt && gesamt.provisional > 0 ? `davon ${n(gesamt.provisional)} ${TERMS.vorlaeufig.short}` : 'ohne verweigerte'}
              {gesamt && data?.vergleich && <VergleichZeile jetzt={gesamt.anfos} v={data.vergleich.anfos} vgl={data.vergleich} format={(x) => formatNumber(x, x < 10 ? 1 : 0)} />}
            </>
          }
        />
        <Kachel
          wert={kachelWert((t) => n(t.verweigert))}
          label={TERMS.verweigert.label}
          tip={`${TERMS.verweigert.tip} ${TERMS.verweigertAnteil.tip}`}
          sub={
            <>
              {gesamt ? `${formatPct(gesamt.verweigert_anteil)} der ${TERMS.bestellungenAlle.label}` : null}
              {gesamt && data?.vergleich && <VergleichZeile jetzt={gesamt.verweigert} v={data.vergleich.verweigert} vgl={data.vergleich} format={(x) => formatNumber(x, x < 10 ? 1 : 0)} mehrIstSchlecht />}
            </>
          }
        />
        <Kachel
          wert={kachelWert((t) => fest(tEuro(t.value_sum)))}
          label={TERMS.wertBeiBestellung.short}
          tip={`${TERMS.wertSumme.tip} ${TERMS.wertBeiBestellung.tip}`}
          sub={
            <>
              {gesamt ? `netto · Ø ${fest(formatEur(gesamt.value_per_anfo, 0))} je Anfo` : null}
              {gesamt && data?.vergleich && <VergleichZeile jetzt={gesamt.value_sum} v={data.vergleich.value_sum} vgl={data.vergleich} format={(x) => fest(tEuro(x))} />}
            </>
          }
        />
      </Box>

      <VerweigerteKarte />

      <UebersichtKarte
        {...karten}
        dimension="produkt"
        titel="Je Produkt"
        nameLabel={TERMS.produktDerAnfo.label}
        nameTip={TERMS.produktDerAnfo.tip}
        howToRead="Welche Produkte im Zeitraum bestellt wurden. Bestellungen zählen die verweigerten mit, alle Quoten rechnen mit den Anfos."
      />
      <UebersichtKarte
        {...karten}
        dimension="kanal"
        titel="Je Kanal"
        nameLabel={TERMS.kanalDerAnfo.label}
        nameTip={TERMS.kanalDerAnfo.tip}
        howToRead={
          <>
            Woher die Bestellungen kamen, erkannt an der Kennung im Bestell-Link — dieselben Kanäle wie im Kapitel{' '}
            <Link component={RouterLink} to="/auswertungen/kanaele" underline="hover">
              Kanäle &amp; Werbeflächen
            </Link>
            , dort mit Bezahlquote und Veränderung über 3, 6 oder 12 Monate.
          </>
        }
      />
      <UebersichtKarte
        {...karten}
        dimension="blog"
        titel="Je Blog"
        nameLabel={TERMS.blogDerBestellung.label}
        nameTip={TERMS.blogDerBestellung.tip}
        howToRead={<BlogHinweis />}
      />
      <UebersichtKarte
        {...karten}
        dimension="wkz"
        titel="Je Werbeaktion (WKZ)"
        nameLabel={TERMS.werbeaktion.label}
        nameTip={TERMS.werbeaktion.tip}
        howToRead="Je Werbekennzeichen: welches Angebot (Produkt, Preis, Bezugsart) bestellt wurde."
      />

      <Box id="bestellliste" sx={{ mt: 3, scrollMarginTop: 80 }}>
        {/* Paket F (30.10.2026): die Liste kommt aus /plx-orders und rechnet bewusst roh. */}
        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }} data-hinweis="liste-roh">
          Die Liste zeigt jede Bestellung so, wie PL-X sie führt. Anders als die Zahlen oben enthält sie auch
          Bestellungen von Mitarbeiter-Adressen und alte vorläufig ergänzte Anfos. Bestellungen ohne Kennung, die oben
          beim Webinar zählen, stehen hier unter „ohne Kennung“.
        </Typography>
        <OrderList
          onOpenOrder={oeffne}
          onCount={setListenAnzahl}
          filter={listenFilter}
          netto
          suche={suche}
          onSucheChange={setzeSuche}
          startFilter={liste ?? undefined}
          onFilterChange={setzeListe}
        />
      </Box>
      <OrderDetailsModal open={bestellung !== null} onClose={() => setBestellung(null)} ordernumber={bestellung} />
    </ChapterPage>
  );
};

export default Bestellungen;
